import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Lightbulb } from 'lucide-react';
import { useTranslation } from '../../../i18n';
import useEditorAutoGrow from './useEditorAutoGrow';

const splitWords = (sentence) => (sentence || '').split(/\s+/).filter((w) => w.length > 0);

export const getSentencesFromContent = (content) => {
  if (Array.isArray(content?.sentences) && content.sentences.length > 0) {
    return content.sentences;
  }
  if (content?.sentence) return [content.sentence];
  return [];
};

const shuffleWithSeed = (array, seed) => {
  const result = [...array];
  const rand = (s) => {
    const x = Math.sin(s) * 10000;
    return x - Math.floor(x);
  };
  let seedValue = seed;
  for (let i = result.length - 1; i > 0; i--) {
    seedValue++;
    const j = Math.floor(rand(seedValue) * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
};

// Ensure the shuffle is not identical to the original ordering when possible.
const shuffleWordsWithSeed = (words, seed) => {
  if (!words || words.length < 2) return [...(words || [])];
  let seedValue = seed;
  let result = shuffleWithSeed(words, seedValue);
  let attempts = 0;
  while (
    attempts < 8 &&
    result.every((w, i) => w === words[i])
  ) {
    seedValue += 100;
    result = shuffleWithSeed(words, seedValue);
    attempts++;
  }
  return result;
};

const UnjumbleElement = ({
  element,
  isEditor = false,
  studentView = false,
  currentArrangement = null,
  showResult = false,
  perWordResults = null,
  isCorrect = null,
  onArrangementChange,
  onCheck,
  onRetry,
  disabled = false,
  hideCheckButton = false,
  onAutoGrow,
}) => {
  const { t } = useTranslation();
  const { content, style, id } = element;
  const {
    hint = '',
    allowRetry = false,
    fontSize = 18,
    fontFamily = 'Inter',
    tileColor = '#ffffff',
    tileTextColor = '#1f2937',
    tileBorderColor = '#2A4D25',
    actionBarColor = '#f9fafb',
    alignment = 'center',
    showNumbers = false,
    numberColor,
  } = content || {};
  const hasCustomNumberColor = typeof numberColor === 'string' && numberColor.length > 0;
  const numberStyleColor = hasCustomNumberColor ? numberColor : tileTextColor;
  const numberStyleOpacity = hasCustomNumberColor ? 1 : 0.7;
  const backgroundColor = style?.backgroundColor || '#ffffff';
  const isActionBarTransparent = actionBarColor === 'transparent';
  const justifyClass = alignment === 'left'
    ? 'justify-start'
    : alignment === 'right'
      ? 'justify-end'
      : 'justify-center';

  const sentences = useMemo(() => getSentencesFromContent(content), [content]);
  const correctWordsPerSentence = useMemo(
    () => sentences.map((s) => splitWords(s)),
    [sentences]
  );
  const totalWordCount = useMemo(
    () => correctWordsPerSentence.reduce((sum, arr) => sum + arr.length, 0),
    [correctWordsPerSentence]
  );

  // Build scrambled tiles per sentence. Each tile has a stable id so React
  // keys survive reorders. Seed the shuffle from element id + sentence idx so
  // every student in a synced classroom sees the same starting order.
  const scrambledTilesPerSentence = useMemo(() => {
    return correctWordsPerSentence.map((words, sIdx) => {
      if (words.length === 0) return [];
      const tiles = words.map((word, idx) => ({
        id: `${id || 'unjumble'}_s${sIdx}_w${idx}`,
        word,
        originalIndex: idx,
      }));
      const baseSeed = id
        ? id.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0)
        : 1;
      return shuffleWordsWithSeed(tiles, baseSeed + sIdx * 37);
    });
  }, [correctWordsPerSentence, id]);

  // Accept legacy flat Tile[] arrangement (single-sentence) by wrapping in [].
  const normalizeArrangement = useCallback((arr) => {
    if (!Array.isArray(arr) || arr.length === 0) return null;
    if (Array.isArray(arr[0])) return arr;
    return [arr];
  }, []);

  const [tilesPerSentence, setTilesPerSentence] = useState(
    normalizeArrangement(currentArrangement) || scrambledTilesPerSentence
  );
  const [dragCursor, setDragCursor] = useState(null); // { sentence, index } for drag source
  const [dragOverCursor, setDragOverCursor] = useState(null); // { sentence, index }
  const pointerDragRef = useRef(null);
  const scrollRef = useRef(null);
  useEditorAutoGrow({
    enabled: isEditor,
    scrollRef,
    currentHeightPct: element.size?.height || 0,
    contentCount: sentences.length,
    onAutoGrow,
  });

  useEffect(() => {
    const normalized = normalizeArrangement(currentArrangement);
    if (normalized) {
      setTilesPerSentence(normalized);
    } else if (scrambledTilesPerSentence.length > 0) {
      setTilesPerSentence(scrambledTilesPerSentence);
    }
  }, [currentArrangement, scrambledTilesPerSentence, normalizeArrangement]);

  const dragEnabled = !disabled && !showResult && !isEditor;

  const moveTile = useCallback((fromSentence, fromIndex, toSentence, toIndex) => {
    if (fromSentence !== toSentence) return; // no cross-sentence drops
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return;
    setTilesPerSentence((prev) => {
      const row = prev[fromSentence];
      if (!row || fromIndex >= row.length || toIndex >= row.length) return prev;
      const nextRow = [...row];
      const [moved] = nextRow.splice(fromIndex, 1);
      nextRow.splice(toIndex, 0, moved);
      const next = prev.map((r, i) => (i === fromSentence ? nextRow : r));
      onArrangementChange?.(next);
      return next;
    });
  }, [onArrangementChange]);

  // HTML5 drag (mouse)
  const handleDragStart = (e, sentence, index) => {
    if (!dragEnabled) return;
    setDragCursor({ sentence, index });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', `${sentence}:${index}`);
  };

  const handleDragOver = (e, sentence, index) => {
    if (!dragEnabled) return;
    if (!dragCursor || dragCursor.sentence !== sentence) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (index !== dragCursor.index) setDragOverCursor({ sentence, index });
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setDragOverCursor(null);
  };

  const handleDrop = (e, sentence, index) => {
    if (!dragEnabled) return;
    e.preventDefault();
    if (dragCursor && dragCursor.sentence === sentence) {
      moveTile(dragCursor.sentence, dragCursor.index, sentence, index);
    }
    setDragCursor(null);
    setDragOverCursor(null);
  };

  const handleDragEnd = () => {
    setDragCursor(null);
    setDragOverCursor(null);
  };

  // Pointer drag (touch/pen) — HTML5 drag doesn't fire on mobile browsers.
  const handlePointerDown = useCallback((e, sentence, index) => {
    if (!dragEnabled) return;
    if (e.pointerType === 'mouse') return;
    if (pointerDragRef.current) return;
    const tile = tilesPerSentence[sentence]?.[index];
    if (!tile) return;
    e.preventDefault();

    const ghost = document.createElement('div');
    ghost.textContent = tile.word;
    Object.assign(ghost.style, {
      position: 'fixed',
      left: '0',
      top: '0',
      padding: '6px 14px',
      borderRadius: '10px',
      border: `2px solid ${tileBorderColor}`,
      background: tileColor,
      color: tileTextColor,
      font: `500 ${fontSize}px "${fontFamily}", Inter, system-ui, sans-serif`,
      whiteSpace: 'nowrap',
      pointerEvents: 'none',
      boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
      zIndex: '99999',
      opacity: '0.95',
      userSelect: 'none',
      WebkitUserSelect: 'none',
      willChange: 'transform',
    });
    document.body.appendChild(ghost);
    const rect = ghost.getBoundingClientRect();
    ghost.style.transform = `translate(${e.clientX - rect.width / 2}px, ${e.clientY - rect.height / 2}px)`;

    pointerDragRef.current = { fromSentence: sentence, fromIndex: index, ghostEl: ghost, pointerId: e.pointerId };
    setDragCursor({ sentence, index });
  }, [dragEnabled, tilesPerSentence, tileBorderColor, tileColor, tileTextColor, fontFamily, fontSize]);

  useEffect(() => {
    const findCursorAt = (clientX, clientY) => {
      const drag = pointerDragRef.current;
      if (!drag) return null;
      const prevDisplay = drag.ghostEl.style.display;
      drag.ghostEl.style.display = 'none';
      const el = typeof document !== 'undefined' ? document.elementFromPoint(clientX, clientY) : null;
      drag.ghostEl.style.display = prevDisplay;
      const tileEl = el && el.closest ? el.closest('[data-unjumble-index]') : null;
      if (!tileEl) return null;
      return {
        sentence: Number(tileEl.getAttribute('data-unjumble-sentence')),
        index: Number(tileEl.getAttribute('data-unjumble-index')),
      };
    };

    const cleanup = () => {
      const drag = pointerDragRef.current;
      if (!drag) return;
      if (drag.ghostEl && drag.ghostEl.parentNode) drag.ghostEl.parentNode.removeChild(drag.ghostEl);
      pointerDragRef.current = null;
      setDragCursor(null);
      setDragOverCursor(null);
    };

    const onMove = (e) => {
      const drag = pointerDragRef.current;
      if (!drag || e.pointerId !== drag.pointerId) return;
      e.preventDefault();
      const rect = drag.ghostEl.getBoundingClientRect();
      drag.ghostEl.style.transform = `translate(${e.clientX - rect.width / 2}px, ${e.clientY - rect.height / 2}px)`;
      const cursor = findCursorAt(e.clientX, e.clientY);
      if (
        cursor &&
        cursor.sentence === drag.fromSentence &&
        cursor.index !== drag.fromIndex
      ) {
        setDragOverCursor(cursor);
      } else {
        setDragOverCursor(null);
      }
    };

    const onUp = (e) => {
      const drag = pointerDragRef.current;
      if (!drag || e.pointerId !== drag.pointerId) return;
      const cursor = findCursorAt(e.clientX, e.clientY);
      const fromSentence = drag.fromSentence;
      const fromIndex = drag.fromIndex;
      cleanup();
      if (cursor && cursor.sentence === fromSentence && cursor.index !== fromIndex) {
        moveTile(fromSentence, fromIndex, cursor.sentence, cursor.index);
      }
    };

    const onCancel = (e) => {
      const drag = pointerDragRef.current;
      if (!drag) return;
      if (e && e.pointerId != null && e.pointerId !== drag.pointerId) return;
      cleanup();
    };

    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
    };
  }, [moveTile]);

  useEffect(() => () => {
    const drag = pointerDragRef.current;
    if (drag && drag.ghostEl && drag.ghostEl.parentNode) {
      drag.ghostEl.parentNode.removeChild(drag.ghostEl);
    }
    pointerDragRef.current = null;
  }, []);

  const handleCheck = () => onCheck?.(tilesPerSentence);

  const sentenceCorrect = (sIdx) => {
    if (perWordResults && perWordResults[sIdx]) {
      return perWordResults[sIdx].every(Boolean);
    }
    const row = tilesPerSentence[sIdx] || [];
    const correct = correctWordsPerSentence[sIdx] || [];
    return correct.length > 0 && row.length === correct.length && row.every((t, i) => t.word === correct[i]);
  };

  const numSentences = correctWordsPerSentence.length;

  const canCheck = numSentences > 0 && tilesPerSentence.length === numSentences
    && tilesPerSentence.every((row, i) => (row || []).length === (correctWordsPerSentence[i] || []).length);

  // ---- EDITOR PREVIEW ----
  if (isEditor) {
    return (
      <div
        className="w-full h-full flex flex-col rounded-xl border-2 border-gray-200 overflow-hidden shadow-sm"
        style={{ backgroundColor }}
      >
        {hint && (
          <div className="px-4 py-2 border-b border-gray-100 bg-gray-50">
            <p className="text-sm text-gray-600 flex items-center gap-2">
              <Lightbulb size={14} className="text-amber-500" />
              {hint}
            </p>
          </div>
        )}

        <div ref={scrollRef} className="flex-1 min-h-0 p-3 flex flex-col items-center justify-center gap-2 overflow-auto">
          {totalWordCount === 0 ? (
            <p className="text-sm text-gray-400 text-center">
              {t('games.unjumble.enterSentenceInSettings')}
            </p>
          ) : (
            scrambledTilesPerSentence.map((row, sIdx) => (
              <div key={`ed_row_${sIdx}`} className={`w-full flex flex-wrap items-center gap-2 ${justifyClass}`}>
                {showNumbers && (
                  <span
                    className="inline-flex items-center font-medium select-none"
                    style={{
                      color: numberStyleColor,
                      fontSize: `${fontSize}px`,
                      fontFamily,
                      opacity: numberStyleOpacity,
                    }}
                  >
                    {sIdx + 1}.
                  </span>
                )}
                {row.map((tile) => (
                  <span
                    key={tile.id}
                    className="inline-flex items-center rounded-lg border-2 font-medium select-none"
                    style={{
                      backgroundColor: tileColor,
                      color: tileTextColor,
                      borderColor: tileBorderColor,
                      padding: '6px 14px',
                      fontSize: `${fontSize}px`,
                      fontFamily,
                    }}
                  >
                    {tile.word}
                  </span>
                ))}
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  // ---- INTERACTIVE ----
  const wordCorrectness = (sIdx, wIdx) => {
    if (!showResult) return null;
    if (perWordResults && perWordResults[sIdx] && perWordResults[sIdx][wIdx] != null) {
      return perWordResults[sIdx][wIdx];
    }
    const row = tilesPerSentence[sIdx] || [];
    const correct = correctWordsPerSentence[sIdx] || [];
    return row[wIdx]?.word === correct[wIdx];
  };

  return (
    <div
      className="w-full h-full flex flex-col rounded-xl overflow-hidden"
      style={{ backgroundColor: 'transparent' }}
    >
      {hint && (
        <div className="px-4 py-2 border-b border-gray-100 bg-gray-50">
          <p className="text-sm text-gray-600 flex items-center gap-2">
            <Lightbulb size={14} className="text-amber-500" />
            {hint}
          </p>
        </div>
      )}

      {/* Sentence rows */}
      <div className="flex-1 min-h-0 p-3 flex flex-col items-center justify-center gap-3 overflow-auto">
        {tilesPerSentence.map((row, sIdx) => {
          const rowCorrect = showResult && sentenceCorrect(sIdx);
          return (
            <div
              key={`row_${sIdx}`}
              className={`w-full flex flex-wrap items-center gap-2 ${justifyClass} ${
                showResult ? (rowCorrect ? 'opacity-100' : 'opacity-100') : ''
              }`}
            >
              {showNumbers && (
                <span
                  className="inline-flex items-center font-medium select-none"
                  style={{
                    color: numberStyleColor,
                    fontSize: `${fontSize}px`,
                    fontFamily,
                    opacity: numberStyleOpacity,
                  }}
                >
                  {sIdx + 1}.
                </span>
              )}
              {(row || []).map((tile, index) => {
                const correctness = wordCorrectness(sIdx, index);
                let bg = tileColor;
                let color = tileTextColor;
                let borderColor = tileBorderColor;
                if (showResult) {
                  if (correctness === true) {
                    bg = '#dcfce7'; color = '#166534'; borderColor = '#22c55e';
                  } else if (correctness === false) {
                    bg = '#fee2e2'; color = '#991b1b'; borderColor = '#ef4444';
                  }
                }
                const isDragging = dragCursor && dragCursor.sentence === sIdx && dragCursor.index === index;
                const isDragOver = dragOverCursor
                  && dragOverCursor.sentence === sIdx
                  && dragOverCursor.index === index
                  && !(dragCursor && dragCursor.sentence === sIdx && dragCursor.index === index);
                return (
                  <span
                    key={tile.id}
                    data-unjumble-sentence={dragEnabled ? sIdx : undefined}
                    data-unjumble-index={dragEnabled ? index : undefined}
                    draggable={dragEnabled}
                    onDragStart={(e) => handleDragStart(e, sIdx, index)}
                    onDragOver={(e) => handleDragOver(e, sIdx, index)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, sIdx, index)}
                    onDragEnd={handleDragEnd}
                    onPointerDown={dragEnabled ? (e) => handlePointerDown(e, sIdx, index) : undefined}
                    className={`
                      inline-flex items-center rounded-lg border-2 font-medium select-none
                      transition-transform duration-100
                      ${dragEnabled ? 'cursor-grab active:cursor-grabbing' : ''}
                      ${isDragging ? 'opacity-40' : ''}
                      ${isDragOver ? 'scale-105 ring-2 ring-brand-primary' : ''}
                    `}
                    style={{
                      backgroundColor: bg,
                      color,
                      borderColor,
                      padding: '6px 14px',
                      fontSize: `${fontSize}px`,
                      fontFamily,
                      touchAction: dragEnabled ? 'none' : undefined,
                      WebkitUserSelect: 'none',
                      userSelect: 'none',
                    }}
                  >
                    {tile.word}
                  </span>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Action bar — only the Check button; result feedback lives in tile colors */}
      {!showResult && !hideCheckButton && (
      <div
        className={`px-3 py-2 flex items-center justify-end gap-2 ${
          isActionBarTransparent ? '' : 'border-t border-gray-100'
        }`}
        style={{ backgroundColor: actionBarColor }}
      >
        <button
          onClick={handleCheck}
          disabled={!canCheck}
          className={`px-4 py-1.5 text-sm font-medium rounded-lg transition ${
            canCheck
              ? 'bg-indigo-500 text-white hover:bg-indigo-600'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          {t('games.common.check')}
        </button>
      </div>
      )}
    </div>
  );
};

// Validate a multi-sentence arrangement. `arrangement` is Tile[][] (rows per
// sentence). Returns per-word results per sentence and per-sentence correctness.
export const validateUnjumble = (content, arrangement = []) => {
  // Back-compat: allow calling with a plain string as first arg.
  const sentences = typeof content === 'string'
    ? [content]
    : getSentencesFromContent(content);
  // Back-compat: accept legacy flat Tile[] arrangement (single sentence).
  const rows = (Array.isArray(arrangement) && arrangement.length > 0 && !Array.isArray(arrangement[0]))
    ? [arrangement]
    : (arrangement || []);
  const correctWordsPerSentence = sentences.map(splitWords);
  const perWordResults = correctWordsPerSentence.map((correct, sIdx) => {
    const row = rows[sIdx] || [];
    return correct.map((_, i) => row[i]?.word === correct[i]);
  });
  const sentenceCorrect = perWordResults.map((row, sIdx) => {
    const correct = correctWordsPerSentence[sIdx];
    const attempt = rows[sIdx] || [];
    return correct.length > 0 && attempt.length === correct.length && row.every(Boolean);
  });
  const totalSentences = correctWordsPerSentence.length;
  const correctSentences = sentenceCorrect.filter(Boolean).length;
  const isCorrect = totalSentences > 0 && correctSentences === totalSentences;
  return { perWordResults, sentenceCorrect, isCorrect, correctSentences, totalSentences };
};

export default UnjumbleElement;
